import { PathsEnum } from 'pages/Paths'
import { IUserRoleGuidEnum } from 'features/settings/settingsTypes'
import { getTntIdFromUrl, getTntIdFromStorage } from 'utils/tenantUrlUtils'

/**
 * Normalizes claim detail URLs to a consistent format
 * Extracts tenant ID and ensures proper /claims/claim/ structure
 */
export const normalizeClaimDetailPath = (path: string): string => {
    const segments = path.split('/').filter(Boolean)
    if (!segments.length) return path

    // Check if first segment is a tenant ID
    const hasTenant = segments[0] && !['claims', 'claim', 'account', 'analytics', 'data-handling', 'file-share', 'files-center', 'batch'].includes(segments[0])
    const tenantPrefix = hasTenant ? `/${segments[0]}` : ''
    const pathWithoutTenant = hasTenant ? `/${segments.slice(1).join('/')}` : path

    const CLAIMS_PATH = '/claims'
    const CLEAN_PREFIX = `${CLAIMS_PATH}/claim/`

    // Already in correct format
    if (pathWithoutTenant.startsWith(CLEAN_PREFIX)) {
        return path
    }

    const pathSegments = pathWithoutTenant.split('/').filter(Boolean)
    if (!pathSegments.length) return path

    const claimsIndex = pathSegments.indexOf('claims')
    if (claimsIndex === -1) return path

    const claimIndex = pathSegments.lastIndexOf('claim')
    if (claimIndex === -1 || claimIndex <= claimsIndex) return path
    if (!pathSegments[claimIndex + 1]) return path

    const tail = pathSegments.slice(claimIndex)
    const normalizedSegments = [pathSegments[claimsIndex], ...tail]

    return tenantPrefix + '/' + normalizedSegments.join('/')
}

/**
 * Normalizes old URL formats to the new tenant-based format
 */
export const normalizeOldUrlFormat = (pathname: string): string => {
    let normalizedPath = pathname

    // Remove /all/tab-name/ segments from claims URLs
    // Example: /claims/all/needs-work/... -> /claims/...
    normalizedPath = normalizedPath.replace(/\/(claims|claim)\/all\/[^/]+\//g, '/$1/')

    // Convert /claim/... to /claims/claim/...
    if (normalizedPath.startsWith('/claim/')) {
        normalizedPath = '/claims' + normalizedPath
    }

    return normalizedPath
}

/**
 * Extracts claim ID from URL path
 */
export const extractClaimIdFromPath = (pathname: string): string | null => {
    const claimIdMatch = pathname.match(/\/claim\/([^/]+)/)
    return claimIdMatch?.[1] || null
}

/**
 * Gets the appropriate tenant ID (prioritizes URL, falls back to storage)
 */
export const getTenantIdForRouting = (): string | null => {
    return getTntIdFromUrl() || getTntIdFromStorage()
}

/**
 * Determines if a route is a restricted path (login, logout, etc.)
 */
export const isRestrictedRoute = (pathname: string): boolean => {
    const restrictedPaths = [PathsEnum.login, PathsEnum.logout, PathsEnum.down, PathsEnum.home]
    return restrictedPaths.some((path) => pathname.includes(path))
}

/**
 * Gets the default route path based on user permissions
 */
export const getDefaultRouteByPermission = (
    permissions: string[] | undefined,
    tntId: string | null,
    prevRoute?: string
): string => {
    // Don't use prevRoute - it may be a route user doesn't have access to
    // Always determine route based on current permissions

    // No permissions or no tenant ID
    if (!permissions || !tntId) {
        return PathsEnum.home
    }

    // Check permissions in priority order
    if (permissions.includes(IUserRoleGuidEnum.claimAccessPermission)) {
        return `/${tntId}/claims`
    }

    if (permissions.includes(IUserRoleGuidEnum.batchUploadPermission)) {
        return `/${tntId}/batch`
    }

    if (permissions.includes(IUserRoleGuidEnum.fileSharePermission)) {
        return `/${tntId}/file-share`
    }

    return PathsEnum.home
}

/**
 * Gets the fallback route when tenant ID might not be available yet
 */
export const getFallbackRoute = (
    permissions: string[] | undefined,
    prevRoute?: string
): string => {
    if (prevRoute && prevRoute !== PathsEnum.root) {
        return prevRoute
    }

    if (!permissions) {
        return PathsEnum.home
    }

    if (permissions.includes(IUserRoleGuidEnum.claimAccessPermission)) {
        return PathsEnum.home // Will be redirected to claims once tntId is available
    }

    if (permissions.includes(IUserRoleGuidEnum.batchUploadPermission)) {
        return PathsEnum.home
    }

    if (permissions.includes(IUserRoleGuidEnum.fileSharePermission)) {
        return PathsEnum.home
    }

    return PathsEnum.home
}

/**
 * Checks if a pathname represents an unauthenticated route
 */
export const isUnauthenticatedRoute = (pathname: string): boolean => {
    const unauthenticatedRoutes = [PathsEnum.login, PathsEnum.logout, PathsEnum.down, PathsEnum.home]
    return unauthenticatedRoutes.some(route => pathname === route || pathname.includes(route)) || pathname === '/'
}
